import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

export type DeviceFileShare = {
  writeBase64File: (path: string, base64: string) => Promise<string>;
  shareFile: (uri: string, title: string) => Promise<void>;
};

const SHARE_CANCELED = 'Share canceled';

export const getCacheDownloadPath = (fileName: string, now = Date.now()) => {
  const base = fileName.split(/[/\\]/).pop()?.trim() ?? '';
  const safe = base.replace(/[^\p{L}\p{N}._-]+/gu, '_').replace(/^\.+/, '');
  return `downloads/${now}-${safe || 'download'}`;
};

export const blobToBase64 = async (blob: Blob) => {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return btoa(binary);
};

const isShareCancellation = (error: unknown) =>
  (error instanceof Error ? error.message : typeof error === 'string' ? error : '').includes(
    SHARE_CANCELED,
  );

export const saveBlobOnDevice = async (
  blob: Blob,
  fileName: string,
  device: DeviceFileShare,
  now = Date.now(),
) => {
  const uri = await device.writeBase64File(
    getCacheDownloadPath(fileName, now),
    await blobToBase64(blob),
  );
  try {
    await device.shareFile(uri, fileName);
  } catch (error) {
    if (isShareCancellation(error)) return;
    throw error;
  }
};

const capacitorDeviceShare: DeviceFileShare = {
  writeBase64File: async (path, base64) => {
    const { uri } = await Filesystem.writeFile({
      path,
      data: base64,
      directory: Directory.Cache,
      recursive: true,
    });
    return uri;
  },
  shareFile: async (uri, title) => {
    await Share.share({ title, files: [uri], dialogTitle: title });
  },
};

const saveBlobWithAnchor = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  link.click();
  URL.revokeObjectURL(url);
};

// WKWebView and Android WebView ignore <a download>.
// ponytail: the whole file is base64'd across the bridge; multi-hundred-MB archives can OOM. Upgrade path: stream to disk in native code.
export const downloadBlob = async (blob: Blob, fileName: string) => {
  if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Filesystem')) {
    await saveBlobOnDevice(blob, fileName, capacitorDeviceShare);
    return;
  }

  saveBlobWithAnchor(blob, fileName);
};

export const fetchBlobFromUrl = async (url: string) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.status}`);
  }
  return response.blob();
};

export const fetchPdfBlobFromUrl = async (url: string) => {
  const blob = await fetchBlobFromUrl(url);

  if (blob.type === 'application/pdf') {
    return blob;
  }

  return new Blob([blob], { type: 'application/pdf' });
};
